import { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import clsx from 'clsx'

interface LayoutProps {
  children: ReactNode
}

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: '📊' },
  { name: 'Models', href: '/models', icon: '🤖' },
  { name: 'API Keys', href: '/api-keys', icon: '🔑' },
  { name: 'Devices', href: '/devices', icon: '📱' },
  { name: 'Usage', href: '/usage', icon: '📈' },
  { name: 'Billing', href: '/billing', icon: '💳' },
  { name: 'Invitation', href: '/invitations', icon: '📨' },
  { name: 'Docs', href: '/docs', icon: '📚' },
]

const adminNavigation = [
  { name: 'Users', href: '/admin/users', icon: '👥' },
  { name: 'Channels', href: '/admin/channels', icon: '🔌' },
  { name: 'Models', href: '/admin/models', icon: '🤖' },
  { name: 'Analytics', href: '/admin/analytics', icon: '📉' },
  { name: 'Invitations', href: '/admin/invitations', icon: '📨' },
]

export function MainLayout({ children }: LayoutProps) {
  const location = useLocation()
  const { user, logout } = useAuthStore()
  
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/dashboard" className="text-xl font-bold text-primary-600">
                AI Gateway
              </Link>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                Balance: <span className="font-semibold text-gray-900">${user?.balance?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="text-sm text-gray-600">
                {user?.email}
              </div>
              <button
                onClick={logout}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        <aside className="w-64 bg-white border-r border-gray-200 min-h-[calc(100vh-4rem)]">
          <nav className="p-4 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={clsx(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-md',
                  location.pathname === item.href
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-50'
                )}
              >
                <span className="mr-3">{item.icon}</span>
                {item.name}
              </Link>
            ))}
            
            {isAdmin && (
              <>
                <div className="pt-4 pb-2">
                  <p className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Admin
                  </p>
                </div>
                {adminNavigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={clsx(
                      'flex items-center px-3 py-2 text-sm font-medium rounded-md',
                      location.pathname === item.href
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    )}
                  >
                    <span className="mr-3">{item.icon}</span>
                    {item.name}
                  </Link>
                ))}
              </>
            )}
          </nav>
        </aside>

        <main className="flex-1 p-6">
          {children}
        </main>
      </div>
    </div>
  )
}
